import { type FC, type RefObject, useState } from 'react'
import { Radio } from '@/components/commons/radio'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalXCloseButton
} from '@/components/ui/modal-atoms'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/translations/i18n'

interface ServiceVettingMerchSubmitModalProps {
	isSubmitting: boolean
	type: 'ai' | 'host-edits'
	onClose: () => void
	submissionRef: RefObject<HTMLButtonElement | null>
	setRequireHostApprovalCb: (value: boolean) => void
}

export const ServiceVettingMerchSubmitModal: FC<
	ServiceVettingMerchSubmitModalProps
> = ({
	isSubmitting,
	onClose,
	submissionRef,
	type,
	setRequireHostApprovalCb
}) => {
	const { t } = useTranslation()
	const [hasSelectedOption, setHasSelectedOption] = useState(false)

	const handleRadioChange = (value: boolean) => {
		setHasSelectedOption(true)
		setRequireHostApprovalCb(value)
	}

	switch (type) {
		case 'ai':
			return (
				<Modal onOverlayClick={onClose} width={480}>
					<ModalHeader className='grid grid-cols-[36px_minmax(0,1fr)_36px] items-center justify-items-center border-neutral-300 border-b'>
						<ModalXCloseButton onClick={onClose} />
						<p className='text-neutral-900'>
							{t('vetting:merch.commons.submission_modal_header')}
						</p>
					</ModalHeader>
					<ModalBody className='gap-1'>
						<h3 className='font-medium text-neutral-900 text-xl'>
							{t(
								'vetting:merch.service.confirmation_ai_modal.title'
							)}
						</h3>
						<p className='text-neutral-900'>
							{t(
								'vetting:merch.service.confirmation_ai_modal.description'
							)}
						</p>
					</ModalBody>
					<ModalFooter>
						<ButtonLegacy
							onClick={onClose}
							type='button'
							variant={'tertiary'}
						>
							{t('common:cancel')}
						</ButtonLegacy>

						<ButtonBrandLegacy
							disabled={isSubmitting}
							onClick={() => submissionRef.current?.click()}
						>
							{t('common:confirm')}
							{isSubmitting && <Spinner size={20} />}
						</ButtonBrandLegacy>
					</ModalFooter>
				</Modal>
			)

		case 'host-edits':
			return (
				<Modal onOverlayClick={onClose} width={480}>
					<ModalHeader className='grid grid-cols-[36px_minmax(0,1fr)_36px] items-center justify-items-center border-neutral-300 border-b'>
						<ModalXCloseButton onClick={onClose} />
						<p className='text-neutral-900'>
							{t('vetting:merch.commons.submission_modal_header')}
						</p>
					</ModalHeader>
					<ModalBody className='gap-1'>
						<div className='flex flex-col gap-4'>
							<h3 className='font-medium text-neutral-900 text-xl'>
								{t(
									'vetting:merch.service.confirmation_edits_modal.title'
								)}
							</h3>
							<Radio
								label={
									<div className='flex flex-col gap-1'>
										<p className='text-neutral-900'>
											{t(
												'vetting:merch.service.confirmation_edits_modal.radio_label_require_host_approval'
											)}
										</p>
										<small>
											{t(
												'vetting:merch.service.confirmation_edits_modal.radio_label_require_host_approval_description'
											)}
										</small>
									</div>
								}
								name='require-host-approval'
								onChange={() => handleRadioChange(true)}
							/>
							<Radio
								label={
									<div className='flex flex-col gap-1'>
										<p className='text-neutral-900'>
											{t(
												'vetting:merch.service.confirmation_edits_modal.radio_label_no_edits'
											)}
										</p>
										<small>
											{t(
												'vetting:merch.service.confirmation_edits_modal.radio_label_no_edits_description'
											)}
										</small>
									</div>
								}
								name='require-host-approval'
								onChange={() => handleRadioChange(false)}
							/>
						</div>
					</ModalBody>
					<ModalFooter>
						<ButtonLegacy
							onClick={onClose}
							type='button'
							variant={'tertiary'}
						>
							{t('common:cancel')}
						</ButtonLegacy>

						<ButtonBrandLegacy
							disabled={isSubmitting || !hasSelectedOption}
							onClick={() => submissionRef.current?.click()}
						>
							{t('common:confirm')}
							{isSubmitting && <Spinner size={20} />}
						</ButtonBrandLegacy>
					</ModalFooter>
				</Modal>
			)
		default:
			return null
	}
}
